import { FormControl, IconButton, InputBase, InputLabel, MenuItem, OutlinedInput, Paper, Select, Skeleton } from "@mui/material";
import classes from "./IntegrationWizard.module.scss";
import { tx } from "../lib/ddd/ddd";
import { Helmet } from "react-helmet";
import JSONPretty from "../components/jsonPrettyOverride/JSONPretty";
import Button from "../components/UI/Button";
import Curl from "../components/APIPlaygroundComponents/Curl";
import LoadingDots from "../components/UI/LoadingDots";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import JsonObject from "../components/APIPlaygroundComponents/JsonObject";
import { useQuery } from "@tanstack/react-query";
import { DDD,
    E2Data,
    mergeDDDIAndUI,
    getInvoiceValuesFromMergedDDDIAndUI }  from "../lib/ddd/ddd";
import { Col, Row } from "react-bootstrap";
import { useContext, useEffect, useRef, useState } from "react";
import SettingsIcon from '@mui/icons-material/Settings';
import ApiIcon from '@mui/icons-material/Api';
import { ReactComponent as CsharpIcon } from "../assets/csharp.svg";
import { ReactComponent as JavaIcon } from "../assets/java.svg";
import { ReactComponent as PhpIcon } from "../assets/php.svg";
import { ReactComponent as JavascriptIcon } from "../assets/javascript.svg";
import { CopyBlock, a11yDark, codepen, dracula, monokaiSublime } from 'react-code-blocks';
import textfile from "../assets/charpInvoice.txt";
import CSharpHowTo from "../components/integrationWizard/full/CSharpHowTo";
import CSharpWhatTo from "../components/integrationWizard/full/CSharpWhatTo";
import CSharpFull from "../components/integrationWizard/full/CSharpFull";
import { GenericFormFields } from "../components/integrationWizard/form/GenericFormFields";
import { DetailsCollapse } from "../components/integrationWizard/detailsCollapse/DetailsCollapse";
import StepSmall from "../components/integrationWizard/Step/StepSmall";
import ReceiptIcon from '@mui/icons-material/Receipt';
import ImageDoc from "../components/integrationWizard/ReturnDoc/ImageDoc";
import { OrgIdContext } from "../store/orgId_context";
import JSHowTo from "../components/integrationWizard/full/JSHowTo";
import JSWhatTo from "../components/integrationWizard/full/JSWhatTo";
import JSFull from "../components/integrationWizard/full/JSFull";
import Intro from "../components/technicalOverviewComponents/Intro";
import IntroFull from "../components/integrationWizard/full/IntroFull";
import PHPHowTo from "../components/integrationWizard/full/PHPHowTo";
import PHPWhatTo from "../components/integrationWizard/full/PHPWhatTo";
import PHPFull from "../components/integrationWizard/full/PHPFull";
import { GenericTooltip } from "../components/integrationWizard/form/GenericTooltip";
import { NavLink, useLocation } from "react-router-dom";


const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";
const ddd = new DDD(apiRequestAddress);
const e2Data_EU_WorkflowSteps = new E2Data("EU_WorkflowSteps");
e2Data_EU_WorkflowSteps.orderBy = "Step";
e2Data_EU_WorkflowSteps.filter = 'UserAccessible=true'
const allDocTypes = [
    {
      doc: "PDF",
      description: "Primary PDF document for invoice.",
    },
    {
      doc: "PDFO",
      description: "Original PDF document received by service.",
    },
    {
      doc: "XMLS",
      description:
        "Country specific XML of e-invoice. (e.g.: UBL 2.1 with country specific extension fields.)",
    },
    {
      doc: "XMLP",
      description: "Peppol XML for e-invoice.",
    },
  ];

const IntegrationWizardFull = () => {
    const [language, setLanguage] = useState("C#");
    const [preset, setPreset] = useState("Invoice");
    const [csharpInvoice, setCSharpInvoice] = useState();
    const [mergedJson, setMergedJson] = useState();
    const [editorJson, setEditorJson] = useState();
    const [stepsInfo, setStepsInfo] = useState([]);
    const [stepsOnOff, setStepsOnOff] =  useState([]);
    const [docsOnOff, setDocsOnOff] = useState([]);
    const [curlCommand, setCurlCommand] = useState("");
    const [sendingRequestLoad, setSendingRequestLoad] = useState(false);
    const [response, setResponse] = useState(null);
    const [mandatoryOnly, setMandatoryOnly] = useState(false);

    const {  integrationDevice } = useContext(OrgIdContext);
    const howToRef = useRef();

    const location = useLocation();
    const { state } = location;

    const {isPending : isMinimalPending, data : minimalData, error:minimalError, isError:isMinimalError} = useQuery({
        queryKey: ['APP_GetNewDDDIAndUIMerged', "Normal", "IncludeInfo", "EditableOnly", integrationDevice],
        queryFn: () => DDD.WithConnectionKey(apiRequestAddress,integrationDevice).APP_GetNewDDDIAndUIMerged({Complexity: "Normal", IncludeInfo : true}, {Complexity: "Normal", EditableOnly: true}),
        staleTime: "Infinity",
        enabled : typeof(integrationDevice) === "string"
    })

    const {isPending : isStepsPending, data : stepsData} = useQuery({
        queryKey: ['Data.GetEntityData', e2Data_EU_WorkflowSteps.GetParamsObject()],
        queryFn: () => e2Data_EU_WorkflowSteps.GetData(),
        staleTime: "Infinity",
    })

    const {_details, ...mainForm} = mergedJson ? mergedJson : {};

    useEffect(()=>{
        if(state?.activeLanguage) setLanguage(state?.activeLanguage);
        console.log(state?.scrollTo);
        if(state?.scrollTo === "how-to") howToRef?.current?.scrollIntoView();
    },[location]);

    useEffect(() => {
        if(isMinimalPending || !minimalData)
            return;

        const {DDDI_GetNew, UI_GetNew} = minimalData;
        let deepClone = mergeDDDIAndUI(DDDI_GetNew.Invoice, UI_GetNew.Invoice);

        setMergedJson(deepClone);

        let invoiceWithValues = getInvoiceValuesFromMergedDDDIAndUI(deepClone);

        setEditorJson({"Object" : {...invoiceWithValues}, "Complexity" : "Normal", "Steps" : [], "ReturnDoc": []});
        
    }, [isMinimalPending, minimalData]);

    useEffect(() => {
        if(!isStepsPending && stepsData){
            setStepsInfo(stepsData?.result?.data||[])
        }
    }, [isStepsPending,stepsData]);

    useEffect(() => {
        const jsonString = JSON.stringify(JSON.stringify(editorJson));

        const curlCmd = tx(`curl -X POST https://${window.location.hostname}/api/service/EUeInvoices.DDDI_Save -H "Authorization: IoT ${integrationDevice}:EUeInvoices" -H "Content-Type: application/json" -d ${jsonString}`);
        setCurlCommand(curlCmd);
    }, [editorJson, integrationDevice])

    const filterObject = (obj) => {
        let invoiceWithValues = getInvoiceValuesFromMergedDDDIAndUI(obj);
        let tmp = editorJson ? editorJson : {};
        let nw = {...tmp, "Object" : {...invoiceWithValues}};
        setEditorJson(nw);
    }

    const onAddDetailClick = (detail) =>{
        if(!mergedJson["_details"][detail] || mergedJson["_details"][detail].length ===0)
        return;

        mergedJson["_details"][detail].push(createNewDetailObject(detail));
        setMergedJson({...mergedJson});
        filterObject(mergedJson);
    }

    const onRemoveDetailClick = (detail, index) => {
        mergedJson["_details"][detail].splice(index, 1);
        setMergedJson({...mergedJson});
        filterObject(mergedJson);
    }

    const createNewDetailObject = (detail) =>{
        let ret = JSON.parse(JSON.stringify(mergedJson["_details"][detail][0]));
        for(let fieldName in ret){
            ret[fieldName].Value = ret[fieldName].defaultValue;
        }

        return ret;
    }

    const handleFieldChange1 = (fieldName, value, {fieldConfig, detail, index, e}) =>{
        updateValueByKey1(mergedJson, fieldName, value, detail, index);
        setMergedJson(mergedJson);
        filterObject(mergedJson);
    }

    const updateValueByKey1 = (obj, key, value, detail, index) => {
        if(obj.hasOwnProperty(key)){
            obj[key].Value = value;
        }

        if(detail && obj["_details"][detail] && obj["_details"][detail][index]){
            updateValueByKey1(obj["_details"][detail][index], key, value);
        }
    }

    const onStepChanged = (step, value) =>{
        let newSteps = {...stepsOnOff, [step]:value};
        setStepsOnOff(newSteps);
        refreshStepsJson(newSteps);
    }

    const refreshStepsJson = (steps) =>{
        let arr = [];
        for(let x in steps){
            if(steps[x] === true)
                arr.push(parseInt(x));
        }
        setEditorJson({...editorJson, "Steps" : arr});
    }

    const onDocChange = (doc, value) =>{
        let newDocs = {...docsOnOff, [doc]:value};
        setDocsOnOff(newDocs);
        refreshDocsJson(newDocs);
    }
    const refreshDocsJson = (docs) =>{
        let arr = [];
        for(let x in docs){
            if(docs[x] === true)
                arr.push(x);
        }
        setEditorJson({...editorJson, ReturnDoc : arr});
    }

    const executeJsonCommand = () =>{
        setSendingRequestLoad(true);
        setResponse("");
        const ddd = DDD.WithConnectionKey(apiRequestAddress, integrationDevice);
        ddd.DDDI_Save(editorJson).then((res) => {
            setSendingRequestLoad(false);
            setResponse(res);
        });
    }

    const onLanguageChange = (e) => {
        setLanguage(e.target.value);
    }

    const onPresetChange = (e) => {
        setPreset(e.target.value);
    }


    return (
        <>
        <Helmet>
            <title>Integration Full | DDD Invoices</title>
        </Helmet>
        <div className={classes.container}>
            <div className={classes["page-container"]}>
                <div className={classes["integration-full-title"]}><h5><SettingsIcon/>Select invoice type and language preference</h5></div>
                <div className={`d-flex ${classes["integration-full-panel"]}`} style={{justifyContent:"space-between"}}>
                    <FormControl>
                            <InputLabel id="language-label" sx={{color:"#005683", "&.Mui-focused": {color:"#005683"}}}>Language</InputLabel>
                        <Select
                            labelId="language-label"
                            id="language-select"
                            value={language}
                            onChange={onLanguageChange}
                            sx={{
                                width:"300px", 
                                background:"white", 
                                height:"3rem",
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':{
                                    borderColor:'inherit'
                                },
                            }}
                            input={<OutlinedInput notched label={"Complexity"} />}
                        >
                            <MenuItem value={"C#"}><CsharpIcon className={`${classes["accordionIcon"]} ${classes["integration-full-icons"]}`} />C#</MenuItem>
                            <MenuItem value={"PHP"}><PhpIcon className={`${classes["accordionIcon"]} ${classes["integration-full-icons"]}`} />PHP</MenuItem>
                            <MenuItem value={"Javascript"}><JavascriptIcon className={`${classes["accordionIcon"]} ${classes["integration-full-icons"]}`} />Javascript</MenuItem>
                            <MenuItem value={"Java"} disabled><JavaIcon className={`${classes["accordionIcon"]} ${classes["integration-full-icons"]}`} />Java</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl>
                            <InputLabel id="type-label" sx={{color:"#005683", "&.Mui-focused": {color:"#005683"}}}>Preset</InputLabel>
                        <Select
                            labelId="type-label"
                            id="type-select"
                            value={preset}
                            onChange={onPresetChange}
                            sx={{
                                width:"300px", 
                                background:"white", 
                                height:"3rem",
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':{
                                    borderColor:'inherit'
                                },
                            }}
                            input={<OutlinedInput notched label={"Complexity"} />}
                        >
                            <MenuItem value={"Invoice"}><i className={`${classes["accordionIcon"]} ti-file`}></i>Invoice</MenuItem>
                            <MenuItem value={"AdvancedInvoice"} disabled><i className={`${classes["accordionIcon"]} ti-layers`}></i>Advanced invoice</MenuItem>
                            <MenuItem value={"StornoInvoice"} disabled><i className={`${classes["accordionIcon"]} ti-layers-alt`}></i>Storno inovice</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <div className={classes["integration-full-title"]}><h5><ApiIcon/>Introduction</h5></div>
                <div className={`${classes["integration-full-panel"]}`}><IntroFull/></div>
                <div className={classes["integration-full-title"]}><h5><ApiIcon/>How to send</h5></div>
                <div className={`${classes["integration-full-panel"]}`} id="how-to-panel" ref={howToRef}>
                    {language === "C#" && <CSharpHowTo/>}
                    {language === "Javascript" && <JSHowTo/>}
                    {language === "PHP" && <PHPHowTo/>}
                </div>
                <div className={classes["integration-full-title"]}><h5><ApiIcon/>What to send</h5></div>
                <div className={`${classes["integration-full-panel"]}`}>
                    {language === "C#" && <CSharpWhatTo objectResponse={minimalData?.DDDI_GetNew?.Invoice}/>}
                    {language === "Javascript" && <JSWhatTo objectResponse={minimalData?.DDDI_GetNew?.Invoice}/>}
                    {language === "PHP" && <PHPWhatTo objectResponse={minimalData?.DDDI_GetNew?.Invoice}/>}
                </div>
                <div className={classes["integration-full-title"]}><h5><ApiIcon/>Full example</h5></div>
                <div className={`${classes["integration-full-panel"]}`}>
                    {language === "C#" && <CSharpFull/>}
                    {language === "Javascript" && <JSFull/>}
                    {language === "PHP" && <PHPFull/>}
                </div>
                {/* <div style={{width:"100%"}}>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque a maximus sem, et pretium felis. In fermentum magna id enim condimentum, quis cursus arcu vehicula. Curabitur tincidunt nisi turpis, eu feugiat orci vestibulum sed. Sed efficitur, libero sit amet hendrerit laoreet, velit sapien congue mi, eget maximus mi velit a urna. Suspendisse potenti. Donec dictum nec arcu vel consequat. Quisque eu velit in lacus iaculis auctor. Etiam non dignissim ante. Curabitur scelerisque tortor in lacus sagittis, nec vulputate magna luctus. Sed sit amet facilisis metus. In hac habitasse platea dictumst. Donec nec elit urna. Morbi lorem elit, iaculis id magna vitae, euismod accumsan erat. Mauris leo leo, dignissim non viverra at, accumsan non dui. Praesent vestibulum mi ligula, sodales pharetra velit viverra sit amet. Integer porttitor sit amet nisl vitae consectetur.</div>
                <div style={{width:"100%"}}>Nullam eu enim nisl. Duis varius venenatis dapibus. Duis euismod mollis ipsum quis mollis. Duis ac sodales urna. Nulla ac metus eget mi luctus venenatis. Nunc luctus enim lorem, sit amet tincidunt elit aliquam quis. Sed quis pharetra risus, vitae lacinia ligula. Nam eget metus porttitor quam gravida maximus eu quis tortor. Etiam fringilla interdum semper. Phasellus ante enim, finibus sed tellus id, ultricies viverra nisi. Sed nec commodo metus. Quisque nunc sapien, condimentum vitae ante sit amet, posuere lobortis erat.</div>
                 */}
                <div className={classes["integration-full-title"]}><h5><ApiIcon/>Playground</h5></div>
                <div className={classes["integration-full-title"]} style={{borderBottom:"0px"}}>
                    On this playground you can create new invoices right on this site. Modify the information on the left side.
                    Preview the Invoice object on the right side and execute {tx("DDDI_Save")} API by pressing the Execute button</div>
                <div className={classes["column-container"]}>
                    <div className={classes["integration-full-title"]} ><h5><ReceiptIcon/>Enter Invoice information</h5></div>
                    <div id="enableMandatory" style={{display:"flex", justifyContent:"space-between"}}>
                        <label>Mandatory only 
                        <GenericTooltip tooltip={"Disables fields that are not mandatory for invoices successful execution"} fieldName={"mandatoryOffon"} />
                        </label>
                        <div className="form-check form-switch text-center">
                        <input className="form-check-input" style={{float:"none"}} type="checkbox" role="switch" checked={mandatoryOnly} onChange={() =>setMandatoryOnly(val => !val)}/>
                        </div>
                    </div>
                    <div className={classes["form-wrapper"]} id="invoiceForm" style={{maxHeight:"none"}}>
                        <form>
                        {mergedJson &&
                        <>
                            <GenericFormFields fieldInfo={mainForm} onFieldChange={handleFieldChange1} showFieldName={true} mandatoryOnly={mandatoryOnly}></GenericFormFields>
                        </>
                        }
                        {_details &&
                            Object.entries(_details).map(([detail, forms]) => {
                            return (
                                <DetailsCollapse detail={detail} forms={forms} opened={true} onAddClick={onAddDetailClick}>
                                {forms.map((form, index) => {
                                    return (<div key={detail+index} className={classes["detail"]}>
                                    {forms.length > 1 && 
                                        <div style={{textAlign:"left"}}>
                                        <i className={`${classes["detailCloseBtn"]} ti-close`} onClick={() => onRemoveDetailClick(detail, index)}></i>
                                        </div>
                                    }
                                        <GenericFormFields fieldInfo={form} onFieldChange={handleFieldChange1} showFieldName={true} className={classes["detailRows"]} detail={detail} index={index} mandatoryOnly={mandatoryOnly}></GenericFormFields>
                                        
                                    </div>)
                                })}
                                </DetailsCollapse>
                            )
                            })
                        }
                        </form>
                    </div>
                    <div className={classes["integration-full-title"]}>
                        <h5><ApiIcon/>Select steps to execute</h5>
                    </div>
                    <div className={classes["steps-wrapper"]} style={{maxHeight:"none"}}>
                        {stepsInfo.map((step, index) => 
                            <StepSmall key={step.Id} checked = {stepsOnOff[step.Step]} enabled={true} onStepChange={onStepChanged} stepNumber={step.Step} stepShortDescription={step.StepDescription} stepLongDescription={step.StepUIDescription}></StepSmall>
                        )}
                    </div>
                    <div className={classes["integration-full-title"]}>
                        <h5><ApiIcon/>Select which documents you wish returned</h5>
                    </div>
                    <div>
                        <Row className="p-1 gy-1">
                            {allDocTypes.map((doc) =>
                                <Col>
                                    <ImageDoc key={doc.doc} doc={doc.doc} onDocClicked ={onDocChange} checked ={docsOnOff[doc.doc]}/>
                                </Col>
                            )}
                        </Row>
                    </div>
                    <div style={{marginTop:"5rem"}}></div>
                    {/* {[...Array(50)].map(i => {
                        return(<div><Skeleton style={{height:"55px"}}/></div>)
                    })} */}
                </div>
                <div className={classes["column-container"]}>
                    <div className={`${classes["full-height"]}`}>
                        <div className={`${classes["sticky"]}`}>
                            <div className={classes["steps-wrapper-full"]} style={{textAlign:"center"}}>
                                Preview Invoice JSON Object
                                <div className={`${classes["form-wrapper-json-full"]}`} style={{textAlign:"left"}}>
                                    <JSONPretty json={editorJson}/>
                                </div>
                                This is the command you can execute from your side or click on the execute button bellow
                                <Curl
                                    currentCurl={curlCommand}
                                    currentJson={editorJson}
                                    activeApi="DDDI_Save"
                                    connectionKey={"integrationDevice"}
                                />
                                <Button style={{fontSize:"2rem", marginTop:"1rem", marginBottom:"1rem"}} onClick={executeJsonCommand}><CloudUploadIcon style={{marginRight:"1rem"}}/>Execute</Button>
                                {sendingRequestLoad && (<LoadingDots />)}
                                {!sendingRequestLoad && response!==null && (
                                <div className={`${classes["form-wrapper-json-full"]}`} style={{textAlign : "left", paddingTop : "5px"}} id="execute-json-response">
                                    <JsonObject response={response} />
                                </div>)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    )
}

export default IntegrationWizardFull;